import { computed, reactive, watch, type Ref } from "vue";
import type {
  DeliveryAreaSectionListInput,
  DeliveryAreaSectionCountInput,
  ListDeliveryAreaSectionUnassignedQuery,
  CreateDeliveryAreaSectionMutation,
  CreateDeliveryAreaSectionMutationVariables,
} from "@/generated/graphql";
import {
  ListDeliveryAreaSectionUnassignedDocument,
  CreateDeliveryAreaSectionDocument,
} from "@/generated/graphql";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { localStorageGetCustomerCode, localStorageGetUserId } from "@/common/localStorageUtil";
import { displayPagingRange as calcDisplayPagingRange } from "@/common/common";
import type { DeliveryAreaInput, AreaSelectValue, ShopSelectValue, UnassignedSection, UnassignedItem } from "../types";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseAreaListSectionUnassignedParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  displayConfirmDialogSectionUnassigned: Ref<boolean>;
  areaSelectValues: Ref<AreaSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  deliveryAreaInput: DeliveryAreaInput;
  listAreaSectionUnassigned: Ref<UnassignedSection>;
  selectListCheckbox: Ref<string[]>;
  searchConditionZipCode: Ref<string | null>;
  listLoading: Ref<boolean>;
  listDataTotalCount: Ref<number>;
  displayPagingRangeUnassigned: Ref<string>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useAreaListSectionUnassigned = ({
  errdialog,
  errMessage,
  overlay,
  displayConfirmDialogSectionUnassigned,
  areaSelectValues,
  shopSelectValues,
  deliveryAreaInput,
  listAreaSectionUnassigned,
  selectListCheckbox,
  searchConditionZipCode,
  listLoading,
  listDataTotalCount,
  displayPagingRangeUnassigned,
}: UseAreaListSectionUnassignedParams) => {
  // ページ跨ぎの選択アイテムデータをキャッシュするMap（lot_code → アイテムデータ）
  const selectedItemsMap = reactive(new Map<string, UnassignedItem>());

  // 選択されたlot_codeに対応するフルデータを取得するcomputed（キャッシュから取得）
  const selectedUnassignedItems = computed(() =>
    Array.from(selectedItemsMap.values()).filter(
      (item): item is NonNullable<typeof item> => item != null
    )
  );

  // テーブルの選択変更ハンドラ（ページ跨ぎ対応）
  const onSelectionChange = (newSelection: string[]) => {
    // 現在のページに表示されているアイテムのlot_codeセット
    const currentPageLotCodes = new Set(
      listAreaSectionUnassigned.value
        .filter((item): item is NonNullable<typeof item> => item?.lot_code != null)
        .map(item => item.lot_code!)
    );
    const newSelectionSet = new Set(newSelection);

    // 現在ページのアイテムについて、選択/解除をキャッシュに反映
    for (const lotCode of currentPageLotCodes) {
      if (newSelectionSet.has(lotCode)) {
        // 選択された → キャッシュに追加
        if (!selectedItemsMap.has(lotCode)) {
          const item = listAreaSectionUnassigned.value.find(i => i?.lot_code === lotCode);
          if (item) {
            selectedItemsMap.set(lotCode, item);
          }
        }
      } else {
        // 選択解除された → キャッシュから削除
        selectedItemsMap.delete(lotCode);
      }
    }

    // selectListCheckboxをキャッシュの全キーで更新
    selectListCheckbox.value = Array.from(selectedItemsMap.keys());
  };

  const pageCount= computed(() =>
    Math.max(1, Math.ceil(listDataTotalCount.value / (unassignedListOptions.itemsPerPage || 1)))
  );

  const unassignedListOptions = reactive({
    page: 1,
    itemsPerPage: 20,
    sortBy: [] as string[],
    sortDesc: [] as boolean[],
    groupBy: [] as string[],
    groupDesc: [] as boolean[],
    multiSort: false,
    mustSort: false,
  });

  // 未登録配達区画オプション変更時
  watch(
    () => unassignedListOptions,
    async () => {
      listLoading.value = true;
      await onClickSearch(true);
      listLoading.value = false;
    },
    { deep: true }
  );

  // 未登録配達区画一覧 オプション更新
  const onUpdateUnassignedOptions = (newOptions: { page: number; itemsPerPage: number; sortBy: Array<{ key: string; order: "asc" | "desc" }> }): void => {
    unassignedListOptions.page = newOptions.page;
    unassignedListOptions.itemsPerPage = newOptions.itemsPerPage;
    unassignedListOptions.sortBy = newOptions.sortBy.map(s => s.key);
  };

  // 一括登録 GraphQL 実行
  const bulkInsertExecute = async (
    customer_code: string,
    chain_code: string,
    shop_code: string,
    area_code: string,
    lot_list: string[],
    user_id: string
  ): Promise<boolean> => {
    try {
      const apolloClient = await generateApolloClient();

      // 配達区画登録
      const result = await apolloClient.mutate<CreateDeliveryAreaSectionMutation, CreateDeliveryAreaSectionMutationVariables>({
        mutation: CreateDeliveryAreaSectionDocument,
        variables: { input: { customer_code, chain_code, shop_code, area_code, lot_list, user_id } },
      });
      if (!result.data) {
        console.error("一括登録エラー");
        return false;
      }
      return true;
    } catch (err: unknown) {
      console.error(err instanceof Error ? err.message : err);
      return false;
    }
  };

  // 絞り込み検索
  const onClickSearch = async (pagingFlg?: boolean | null): Promise<void> => {
    if (!areaSelectValues.value.area_code) return;
    if (pagingFlg !== true) {
      unassignedListOptions.page = 1;
      // 新規検索時は選択状態とキャッシュをクリア
      selectedItemsMap.clear();
      selectListCheckbox.value = [];
    }

    // 配達区画未登録一覧 input [limit 取得件数 offset:取得開始位置]
    const listInput: DeliveryAreaSectionListInput = {
      area_code: areaSelectValues.value.area_code,
      chain_code: deliveryAreaInput.chain_code,
      customer_code: localStorageGetCustomerCode(),
      shop_code: shopSelectValues.value.shop_code,
      zip_code: searchConditionZipCode.value || undefined,
      limit: unassignedListOptions.itemsPerPage,
      offset: (unassignedListOptions.page - 1) * unassignedListOptions.itemsPerPage,
    };

    // 配達区画未登録件数 input
    const countInput: DeliveryAreaSectionCountInput = {
      area_code: listInput.area_code,
      chain_code: listInput.chain_code,
      customer_code: listInput.customer_code,
      shop_code: listInput.shop_code,
      zip_code: listInput.zip_code,
    };

    try {
      const apolloClient = await generateApolloClient();

      // 未登録配達区画一覧取得
      const result = await apolloClient.query<ListDeliveryAreaSectionUnassignedQuery>({
        query: ListDeliveryAreaSectionUnassignedDocument,
        variables: { input: listInput, countInput },
        fetchPolicy: "no-cache",
      });
      if (!result.data.listDeliveryAreaSectionUnassigned || !result.data.getDeliveryAreaSectionUnassignedCount) {
        console.error("未登録配達区画取得エラー");
        return;
      }
      listAreaSectionUnassigned.value = result.data.listDeliveryAreaSectionUnassigned;
      listDataTotalCount.value = result.data.getDeliveryAreaSectionUnassignedCount.count;
      const maxPage = pageCount.value;
      if (unassignedListOptions.page > maxPage) {
        unassignedListOptions.page = maxPage;
      }
      displayPagingRangeUnassigned.value = calcDisplayPagingRange(
        unassignedListOptions.page - 1,
        unassignedListOptions.itemsPerPage,
        listDataTotalCount.value
      );
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
    }
  };

  // 一括追加確認
  const onClickCheckUnassignedSection = async (): Promise<void> => {
    if (selectListCheckbox.value.length === 0) return;
    displayConfirmDialogSectionUnassigned.value = true;
  };

  // 一括追加実行
  const onClickSectionUnassignedComplete = async (): Promise<void> => {
    overlay.value = true;
    try {
      // selectListCheckboxは既にlot_codeの配列なので、そのまま使用
      const lot_list = selectListCheckbox.value;
      const success = await bulkInsertExecute(
        localStorageGetCustomerCode(),
        deliveryAreaInput.chain_code,
        shopSelectValues.value.shop_code,
        areaSelectValues.value.area_code,
        lot_list,
        localStorageGetUserId()
      );

      //画面を閉じる
      displayConfirmDialogSectionUnassigned.value = false;
      overlay.value = false;

      if (success) {
        selectListCheckbox.value = [];
        selectedItemsMap.clear();
        await onClickSearch();
      } else {
        errMessage.value = `ロットコード ${lot_list.join(",")} の登録に失敗しました。`;
        errdialog.value = true;
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
      overlay.value = false;
    }
  };

  return {
    selectedUnassignedItems,
    pageCount,
    unassignedListOptions,
    onUpdateUnassignedOptions,
    onClickSearch,
    onClickCheckUnassignedSection,
    onClickSectionUnassignedComplete,
    onSelectionChange,
  };
};
